import {
  REQUEST_PLACEMENTS_SUCCESS,
  REQUEST_PLACEMENTS_FAILURE,
  CREATE_CUSTOM_PLACEMENT_SUCCESS,
  DELETE_CUSTOM_PLACEMENT_SUCCESS,
  REQUEST_TOURS_SUCCESS,
  UPDATE_TOURS_SUCCESS,
  LOADING_TOURS,
  REQUEST_PRINT_MODES_SUCCESS,
  REQUEST_THEMES_SUCCESS,
  ADD_THEMES_SUCCESS,
  GET_PRICING_TEMPLATES_SUCCESS,
  GET_PRICING_TEMPLATES_FAILURE,
  ADD_PRICING_TEMPLATES_SUCCESS,
  ADD_PRICING_TEMPLATES_FAILURE,
  GET_ORDER_STATUS_COLOR_TAGS_SUCCESS,
  ADD_ORDER_STATUS_COLOR_TAGS_SUCCESS,
  GET_ENTERPRISE_LOCATION_STATUS_SUCCESS,
  UPDATE_ENTERPRISE_LOCATION_STATUS_SUCCESS,
} from "../../constants/actionTypes";
import { domainName } from "../../utils";
import Color from "color";

// import defaultLogo from "../../assets/images/Logo.png";
const DEFAULT_PRIMARY_COLOR = "#007bff"; // Example primary color
// const DEFAULT_PRIMARY_GRADIENT = "linear-gradient(to right, #3498db, #2ecc71)";
const DEFAULT_LIGHTER_PRIMARY_COLOR = "#f2f4ea";

/**
 * Build a near-white tint of the brand color for use as a soft background
 * (timeline strip, pickup callout, CTA card, etc.). The previous implementation
 * used `Color(c).lighten(0.9)`, which is multiplicative on HSL lightness and
 * stays black for a black brand — producing black-on-black surfaces on the
 * quote/PO pages. Setting lightness to an absolute high value preserves the
 * brand hue when present and degrades cleanly to a light gray for black or
 * other very-dark brands. Wrapped in try/catch in case `primaryGradient`
 * contains a CSS gradient string `Color()` can't parse.
 */
const computeLighterPrimaryColor = (input) => {
  try {
    return Color(input).lightness(94).hex();
  } catch (_e) {
    return DEFAULT_LIGHTER_PRIMARY_COLOR;
  }
};

const ORDER_STATUS_COLORS = [
  { hours: 0, color: "#008000", key: "1", label: "less than a day old" },
  { hours: 24, color: "#FFA500", key: "2", label: "a day old" },
  { hours: 48, color: "#FF0000", key: "3", label: "more than a day old" },
];

const INITIAL_STATE = {
  placements: {},
  discounts: {},
  activePlacements: {},
  customPlacements: {},
  toursCompleted: {},
  tourLoading: false,
  printModes: [],
  themeData: {},
  pricingTemplate: {},
  pricingsTemplates: [],
  orderStatusColor: ORDER_STATUS_COLORS,
};

/** Theme payloads may use camelCase or legacy snake_case for the help URL. */
const normalizeThemePayload = (data) => {
  const raw = data && typeof data === "object" ? data : {};
  if (!("help_desk_link" in raw) && !("helpDeskLink" in raw)) return raw;
  const desk = raw.helpDeskLink ?? raw.help_desk_link;
  return { ...raw, helpDeskLink: desk };
};

const buildThemeData = (data) => {
  const theme = normalizeThemePayload(data);

  return {
    primaryColor:
      theme?.primaryGradient ||
      theme?.primaryColor ||
      DEFAULT_PRIMARY_COLOR,
    bgColor: {
      background:
        theme?.primaryGradient ||
        theme?.primaryColor ||
        DEFAULT_PRIMARY_COLOR,
    },
    secondaryColor: theme?.secondaryColor,
    navigationHighLightTextColor: theme?.navigationHighLightTextColor,
    navigationTextColor: theme?.navigationTextColor,
    color: { color: theme?.tertiaryColor || theme?.secondaryColor },
    logo: theme?.logo,
    logoDark: theme?.logoDark,
    logoSymbol: theme?.logoSymbol,
    enterprise: theme?.enterprise || {
      name: domainName,
    },
    lighterPrimaryColor: computeLighterPrimaryColor(
      theme?.primaryColor ||
        theme?.primaryGradient ||
        DEFAULT_PRIMARY_COLOR
    ),
    ...theme,
  };
};

export default function settings(state = INITIAL_STATE, action) {
  switch (action.type) {
    case REQUEST_PLACEMENTS_SUCCESS:
      const placements = action?.payload?.data?.placements || {};
      const discounts = action?.payload?.data?.discounts || {};
      const activePlacements = action?.payload?.data?.activePlacements || {};
      const customPlacements = action?.payload?.data?.customPlacements || {};

      return {
        ...state,
        placements,
        discounts,
        activePlacements,
        customPlacements,
      };

    case REQUEST_PLACEMENTS_FAILURE:
      return { ...state, error: "Record not found" };

    case CREATE_CUSTOM_PLACEMENT_SUCCESS: {
      const created = action?.payload?.data || {};
      if (!created?.id || !created?.name) return state;
      return {
        ...state,
        placements: {
          ...(state.placements || {}),
          [created.id]: created.name,
        },
        activePlacements: {
          ...(state.activePlacements || {}),
          [created.id]: created.name,
        },
        customPlacements: {
          ...(state.customPlacements || {}),
          [created.id]: created,
        },
      };
    }

    case DELETE_CUSTOM_PLACEMENT_SUCCESS: {
      const deletedId = String(action?.payload?.data?.id || "");
      if (!deletedId) return state;
      const nextPlacements = { ...(state.placements || {}) };
      const nextActive = { ...(state.activePlacements || {}) };
      const nextCustom = { ...(state.customPlacements || {}) };
      delete nextPlacements[deletedId];
      delete nextActive[deletedId];
      delete nextCustom[deletedId];
      return {
        ...state,
        placements: nextPlacements,
        activePlacements: nextActive,
        customPlacements: nextCustom,
      };
    }

    case REQUEST_TOURS_SUCCESS:
      return {
        ...state,
        toursCompleted: action?.payload?.data?.toursCompleted || {},
      };

    case UPDATE_TOURS_SUCCESS:
      const upadtedToursCompleted = {
        ...(state?.toursCompleted || {}),
        ...(action?.payload?.data?.toursCompleted || {}),
      };

      return {
        ...state,
        toursCompleted: upadtedToursCompleted,
      };

    case LOADING_TOURS:
      return {
        ...state,
        tourLoading: action.payload,
      };

    case REQUEST_PRINT_MODES_SUCCESS:
      const pm = action?.payload?.print_modes || [];
      return {
        ...state,
        printModes: ["", ...pm],
      };

    case REQUEST_THEMES_SUCCESS:
      return {
        ...state,
        themeData: buildThemeData(action?.payload?.data),
      };

    case ADD_THEMES_SUCCESS:
      return {
        ...state,
        themeData: buildThemeData(action?.payload?.data),
      };

    case GET_PRICING_TEMPLATES_SUCCESS:
      return {
        ...state,
        pricingsTemplates: action?.payload?.data?.pricingsTemplates || [],
      };

    case GET_PRICING_TEMPLATES_FAILURE:
      return { ...state, error: "Record not found", pricingsTemplates: [] };

    case ADD_PRICING_TEMPLATES_SUCCESS: {
      const existing = Array.isArray(state.pricingsTemplates)
        ? state.pricingsTemplates
        : Object.values(state.pricingsTemplates || {});
      const created = action?.payload?.data;
      return {
        ...state,
        pricingsTemplates: created ? [...existing, created] : existing,
      };
    }

    case ADD_PRICING_TEMPLATES_FAILURE:
      return { ...state, error: "Failed to Add Template" };

    case GET_ORDER_STATUS_COLOR_TAGS_SUCCESS:
      const orderStatusColor =
        action?.payload?.data?.statusColors || ORDER_STATUS_COLORS;
      return { ...state, orderStatusColor };

    case ADD_ORDER_STATUS_COLOR_TAGS_SUCCESS:
      const updatedStatusColors =
        action?.payload?.data?.statusColors || ORDER_STATUS_COLORS;
      return { ...state, orderStatusColor: updatedStatusColors };

    case GET_ENTERPRISE_LOCATION_STATUS_SUCCESS:
      return {
        ...state,
        locationStatus: action?.payload?.data || {},
      };

    case UPDATE_ENTERPRISE_LOCATION_STATUS_SUCCESS:
      return {
        ...state,
        locationStatus: action?.payload?.data || {},
      };

    default:
      return state;
  }
}
